import React, { useState } from "react";
import { Box, Divider, Group, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { Alert, Badge, Button } from "@xplatform/design-system";
import { IconAlertCircle, IconDownload, IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { reviewsApi } from "@services/reviewsApi";
import { filenameFromKey, triggerDownload } from "@utils/download";
import type { ReviewDownload } from "@models/reviews";
import { ReviewStatusBadge } from "./ReviewStatusBadge";
import { STATUS_ORDER } from "./reviewStatus";

interface Props {
  downloads: ReviewDownload[];
  onOpen: (download: ReviewDownload) => void;
  /** Verdicts an import could not apply, by download key (PBI #936072). */
  droppedByKey?: Record<string, number>;
}

/**
 * One row per review-file period (PBI #781340): Period / Status / Rows / Download / Upload.
 * Clicking the row or its Upload action opens the per-file detail view.
 */
export const ReviewFilesList: React.FC<Props> = ({ downloads, onOpen, droppedByKey = {} }) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async (d: ReviewDownload) => {
    setDownloading(d.key);
    setError(null);
    try {
      const { url } = await reviewsApi.getDownloadUrl(d.key);
      await triggerDownload(url, filenameFromKey(d.key));
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setDownloading(null);
    }
  };

  // Files still waiting for the reviewer — only "outstanding", as the legend defines it.
  // Pending files are already uploaded and need nothing from them.
  const outstanding = downloads.filter((d) => d.review_status === "outstanding").length;

  return (
    <Paper withBorder p="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>{t("reviews.list.title")}</Title>
          <Badge variant="light" color={outstanding > 0 ? "blue" : "teal"}>
            {t("reviews.status.outstandingCount", { count: outstanding })}
          </Badge>
        </Group>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {downloads.length === 0 ? (
          <Text c="dimmed">{t("reviews.list.empty")}</Text>
        ) : (
          <Table striped highlightOnHover style={{ tableLayout: "fixed", width: "100%" }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("reviews.list.period")}</Table.Th>
                <Table.Th style={{ width: "140px" }}>{t("reviews.list.status")}</Table.Th>
                <Table.Th style={{ width: "90px" }}>{t("reviews.list.rows")}</Table.Th>
                <Table.Th style={{ width: "150px" }} />
                <Table.Th style={{ width: "130px" }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {downloads.map((d) => {
                const status = d.review_status;
                const showRows = status !== "empty" && d.row_count != null && d.row_count >= 0;
                return (
                  <Table.Tr key={d.key} style={{ cursor: "pointer" }} onClick={() => onOpen(d)}>
                    <Table.Td>
                      <Text fw={600}>{d.period}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ReviewStatusBadge status={status} />
                      {droppedByKey[d.key] > 0 && (
                        <Badge color="orange" variant="light" size="xs" mt={4} data-testid="dropped-badge">
                          {t("reviews.list.droppedBadge", { count: droppedByKey[d.key] })}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>{showRows ? d.row_count : "—"}</Table.Td>
                    <Table.Td>
                      {/* Available for every row, including empty ones, so people can check. */}
                      <Button
                        size="xs"
                        variant="light"
                        color="green"
                        leftSection={<IconDownload size={14} />}
                        loading={downloading === d.key}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDownload(d);
                        }}
                      >
                        {t("reviews.list.download")}
                      </Button>
                    </Table.Td>
                    <Table.Td>
                      {/* Hidden for empty (nothing to review), disabled once imported. */}
                      {status !== "empty" && (
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconUpload size={14} />}
                          disabled={status === "imported"}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpen(d);
                          }}
                        >
                          {t("reviews.list.upload")}
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        <Divider />
        <Box>
          <Text fw={600} size="sm" mb="xs">
            {t("reviews.legend.title")}
          </Text>
          <Stack gap={6}>
            {STATUS_ORDER.map((s) => (
              <Group key={s} gap="sm" wrap="nowrap">
                <Box style={{ width: 110 }}>
                  <ReviewStatusBadge status={s} />
                </Box>
                <Text size="sm" c="dimmed">
                  {t(`reviews.legend.${s}`)}
                </Text>
              </Group>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};
